import { combineLatest, map } from 'rxjs';
import { persistentDerivedStream } from '@ardoq/rxbeach';
import {
  APISlideAttributes,
  ArdoqId,
  EnhancedPresentation,
} from '@ardoq/api-types';
import { t, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { slides$ } from 'streams/slides/slides$';
import { loadedPresentation$ } from 'presentation/streams/loadedPresentation$';
import { getSlideTitle } from 'presentation/slideTitle';
import { slideOperations } from 'streams/slides/slideOperations';
import { Slide } from './types';

type SlideTitleArgs = {
  slide: APISlideAttributes | undefined;
  slideNumber: number;
  i18nLocale: I18nLocale;
};

const toSlideTitle = ({
  slide,
  slideNumber,
  i18nLocale,
}: SlideTitleArgs): string => {
  if (!slide) {
    return t(
      i18nLocale,
      'presentationAdmin:viewer.unauthorizedSlideTitle',
      'Unauthorized Slide'
    );
  }
  return (
    getSlideTitle(slide) ||
    t(
      i18nLocale,
      'presentationAdmin:viewer.slideNavigator.untitledSlide',
      { number: slideNumber },
      'Slide {{number}}'
    )
  );
};

const toSlides = (
  presentation: EnhancedPresentation | null,
  slidesById: Record<ArdoqId, APISlideAttributes>,
  i18nLocale: I18nLocale
): Slide[] =>
  (presentation?.slides ?? []).map((id, index) => {
    const slide = slidesById[id];
    return {
      id,
      title: toSlideTitle({ slide, slideNumber: index + 1, i18nLocale }),
      viewId: slide && slideOperations.getSlideViewId(slide),
    };
  });

export const getSelectedSlideTitle = (
  slides: Slide[],
  selectedSlideId: ArdoqId | null
): string => slides.find(({ id }) => id === selectedSlideId)?.title ?? '';

export const presentationSlides$ = persistentDerivedStream<Slide[]>(
  'presentationSlides$',
  combineLatest([loadedPresentation$, slides$, i18nLocale$]).pipe(
    map(([{ presentation }, { byId: slidesById }, i18nLocale]) =>
      toSlides(presentation, slidesById, i18nLocale)
    )
  ),
  []
);
